import { Helmet } from 'react-helmet-async';
import { filter } from 'lodash';
import { useState, useContext, useEffect } from 'react';
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Link,
  TextField,
  CircularProgress,
} from '@mui/material';

// components
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import EditIcon from '@mui/icons-material/Edit';

import { PartieContext } from '../../services/partieContext';

import Iconify from '../../components/iconify';
import Scrollbar from '../../components/scrollbar';

import { Modal, ModalBody } from '../../components/modal/Modal';

// sections
import { ZakazkyListHead, ZakazkyListToolbar } from '../../sections/@dashboard/zakazky';

import { app } from '../../utils/app.config';
import PartieForm from '../../sections/@dashboard/partie/PartieForm';
import { useSearchParams } from 'react-router-dom';

// ----------------------------------------------------------------------

const TABLE_HEAD = [
  { id: 'contactPerson', label: 'Kontaktná osoba', alignRight: false },
  { id: 'phone', label: 'Telefónne číslo', alignRight: false },
  { id: 'mail', label: 'E-mail', alignRight: false },
  { id: '' },
];

// ----------------------------------------------------------------------

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === 'desc'
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  if (query) {
    return filter(
      array,
      (_user) =>
        _user.contactPerson.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _user.phone.toLowerCase().indexOf(query.toLowerCase()) !== -1 ||
        _user.mail.toString().toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }
  return stabilizedThis.map((el) => el[0]);
}

export default function PartieListPage() {
  const { loading, partie, error, addPartia, removePartia, editPartia } = useContext(PartieContext);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('contactPerson');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [modal, setModal] = useState(false);
  const [currentPartia, setCurrentPartia] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (searchParams.get('page')) {
      setPage(Number(searchParams.get('page')));
    }
    if (searchParams.get('rows')) {
      setRowsPerPage(Number(searchParams.get('rows')));
    }
  }, [searchParams]);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setSearchParams((params) => {
      params.set('page', newPage);
      return params;
    });
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
    setSearchParams((params) => {
      params.set('page', 0);
      params.set('rows', parseInt(event.target.value, 10));
      return params;
    });
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const removePartiaHandler = (id) => {
    // Condition if it was saved to database
    if (true) {
      removePartia(id);
    } else {
      console.log('error wasnt removed');
    }
  };

  const addPartiaHandler = () => {
    setModal(true);
    setCurrentPartia(null);
  };

  const editPartiaHandler = (id) => {
    setModal(true);
    setCurrentPartia(partie.find((value) => value.id === id));
  };

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - partie.length) : 0;

  const filteredUsers = applySortFilter(partie, getComparator(order, orderBy), filterName);

  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <>
      <Helmet>
        <title> Zoznam zakázok | {app.title} </title>
      </Helmet>
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Zoznam partií
          </Typography>
          <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={addPartiaHandler}>
            Pridať partiu
          </Button>
        </Stack>

        <Modal
          aria-labelledby="modal-title"
          aria-describedby="modal-desc"
          open={modal}
          onClose={() => setModal(false)}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
        >
          <>
            <PartieForm currentPartia={currentPartia} addPartia={addPartia} editPartia={editPartia} />
          </>
        </Modal>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <ZakazkyListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={partie.length}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                    const { id, contactPerson, mail, phone } = row;

                    return (
                      <TableRow hover key={id} tabIndex={-1} role="checkbox">
                        <TableCell component="th" scope="row" padding="none">
                          <Typography variant="subtitle2" noWrap sx={{ pl: 2 }}>
                            {contactPerson}
                          </Typography>
                        </TableCell>

                        <TableCell align="left">
                          <Link href={`tel:${phone}`}>{phone}</Link>
                        </TableCell>

                        <TableCell align="left">
                          <Link href={`mailto:${mail}`}>{mail}</Link>
                        </TableCell>

                        <TableCell align="right">
                          <IconButton
                            aria-label="delete"
                            color="primary"
                            onClick={() => {
                              editPartiaHandler(id);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton aria-label="delete" color="error" onClick={() => removePartiaHandler(id)}>
                            <DeleteOutlineOutlinedIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>

                {isNotFound && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <Typography variant="h6" paragraph>
                            Žiadna zhoda
                          </Typography>

                          <Typography variant="body2">
                            K hľadanému výrazu &nbsp;
                            <strong>&quot;{filterName}&quot;</strong> sa nenašiel žiaden záznam.
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
                {loading && (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper
                          sx={{
                            textAlign: 'center',
                          }}
                        >
                          <CircularProgress />
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={partie.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={'Počet položiek na jednej strane'}
            labelDisplayedRows={({ from, to, count }) => {
              return '' + from + '-' + to + ' z ' + count;
            }}
          />
        </Card>
      </Container>
    </>
  );
}
